// ============================================================================
// FarmerVerifyPage — #farmer-verify/<token> landing (Task #10).
// ============================================================================
// Idempotent on the server side. On success, also reports how many historical
// registrations got backlinked so the farmer knows their old records will
// appear under "My registrations".
// ============================================================================

import { useEffect, useState } from 'react';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { api } from '../api';

interface Props {
  token: string;
}

export function FarmerVerifyPage({ token }: Props) {
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>('loading');
  const [linked, setLinked] = useState<number | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.farmerVerifyEmail(token)
      .then((r) => {
        if (cancelled) return;
        setStatus('ok');
        setLinked(r.linked_registrations ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus('error');
        setError(err?.message || 'Could not verify email.');
      });
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center">
          <a href="/" className="flex items-center gap-3" aria-label="Up in Farms — home">
            <img src="/logo.jpg" alt="Up in Farms Food Hub" className="h-12 md:h-14 w-auto" />
          </a>
        </div>
      </header>
      <main className="flex-1 max-w-md mx-auto px-4 py-12 w-full">
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="h-10 w-10 text-uif-green-dark mx-auto animate-spin" />
              <p className="text-sm text-gray-600 mt-3">Verifying…</p>
            </>
          )}
          {status === 'ok' && (
            <>
              <CheckCircle2 className="h-12 w-12 text-uif-green mx-auto" />
              <h1 className="text-xl font-bold text-uif-green-dark mt-2">Email verified</h1>
              <p className="text-sm text-gray-700 mt-2">
                You're all set. Sign in to see your registrations
                {linked != null && linked > 0 && (
                  <> &mdash; we found <strong>{linked}</strong> existing registration{linked === 1 ? '' : 's'} under your email and linked them to your new account.</>
                )}
                {linked === 0 && '.'}
              </p>
              <a
                href="#farmer-signin"
                className="inline-block mt-4 rounded-md bg-uif-green text-white font-semibold px-4 py-2 hover:bg-uif-green-dark"
              >
                Sign in
              </a>
            </>
          )}
          {status === 'error' && (
            <>
              <AlertCircle className="h-10 w-10 text-red-600 mx-auto" />
              <h1 className="text-xl font-bold text-red-800 mt-2">Verification link not recognized</h1>
              <p className="text-sm text-gray-700 mt-2">{error}</p>
              <a
                href="#farmer-signin"
                className="inline-block mt-4 rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
              >
                Back to sign in
              </a>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
